import { ReactNode } from 'react';

import { LinkNode, AutoLinkNode } from '@lexical/link';
import {
  DecoratorNode,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { TrackingLink } from './TrackingLink';

export type TrackingLinkAttributes = {
  target?: null | string;
  title?: null | string;
};

export type SerializedTrackingLinkNode = Spread<
  {
    target: string;
    title?: string;
  },
  Spread<TrackingLinkAttributes, SerializedLexicalNode>
>;

export class TrackingLinkNode extends DecoratorNode<ReactNode> {
  __title?: string;
  __target: string;

  static override getType(): string {
    return 'tracking-link';
  }

  static override clone(node: TrackingLinkNode): TrackingLinkNode {
    return new TrackingLinkNode(node.__target, node.__title, node.__key);
  }

  constructor(target: string, title?: string, key?: NodeKey) {
    super(key);
    this.__target = target;
    this.__title = title;
  }

  override createDOM(): HTMLElement {
    const elem = document.createElement('span');
    elem.style.display = 'inline-block';
    return elem;
  }

  override updateDOM(prevNode: TrackingLinkNode): boolean {
    return prevNode.__title !== this.getTitle();
  }

  override exportDOM(): DOMExportOutput {
    const element = document.createElement('span');
    element.setAttribute('data-lexical-tracking-link', 'true');
    element.textContent = this.getTextContent();
    return { element };
  }

  override decorate(): ReactNode {
    return (
      <TrackingLink
        nodeKey={this.__key}
        title={this.getTitle()}
        target={this.getTarget()}
        setTitle={this.setTitle.bind(this)}
      />
    );
  }

  override exportJSON(): SerializedTrackingLinkNode {
    return {
      title: this.getTitle(),
      target: this.getTarget(),
      type: 'tracking-link',
      version: 1,
    };
  }

  static override importJSON(serializedNode: SerializedTrackingLinkNode): TrackingLinkNode {
    const node = $createTrackingLinkNode(serializedNode.target, serializedNode.title);
    return node;
  }

  override getTextContent(): string {
    return this.getTitle() || this.getTarget();
  }

  getTarget(): string {
    return this.getLatest().__target;
  }

  getTitle(): string | undefined {
    return this.getLatest().__title;
  }

  setTitle(title?: string): void {
    const writable = this.getWritable();
    writable.__title = title;
  }
}

export function $createTrackingLinkNode(target: string, title?: string): TrackingLinkNode {
  return new TrackingLinkNode(target, title);
}

export function $isTrackingNode(node: LexicalNode | null | undefined): node is TrackingLinkNode {
  return node instanceof TrackingLinkNode;
}

export const TrackingLinkNodes = [LinkNode, AutoLinkNode, TrackingLinkNode];
